import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export const VIEW_AS_PREVIEW_STORAGE_KEY = "quest-fe-view-as-preview";

export type ViewAsPreviewState = {
  active: boolean;
  studentId: string;
  idpId: string;
  displayName: string;
  returnTo: string;
};

export type StartViewAsPreviewPayload = {
  studentId: string;
  idpId: string;
  displayName: string;
  returnTo: string;
};

function readStoredPreview(): ViewAsPreviewState {
  if (typeof window === "undefined") {
    return {
      active: false,
      studentId: "",
      idpId: "",
      displayName: "",
      returnTo: "/admin",
    };
  }
  try {
    const raw = sessionStorage.getItem(VIEW_AS_PREVIEW_STORAGE_KEY);
    if (!raw) {
      return {
        active: false,
        studentId: "",
        idpId: "",
        displayName: "",
        returnTo: "/admin",
      };
    }
    const parsed = JSON.parse(raw) as StartViewAsPreviewPayload;
    if (!parsed?.studentId || !parsed?.idpId) {
      return {
        active: false,
        studentId: "",
        idpId: "",
        displayName: "",
        returnTo: "/admin",
      };
    }
    return {
      active: true,
      studentId: String(parsed.studentId),
      idpId: String(parsed.idpId),
      displayName: parsed.displayName || "",
      returnTo: parsed.returnTo || "/admin",
    };
  } catch {
    return {
      active: false,
      studentId: "",
      idpId: "",
      displayName: "",
      returnTo: "/admin",
    };
  }
}

function persistPreview(state: ViewAsPreviewState) {
  if (typeof window === "undefined") return;
  if (!state.active) {
    sessionStorage.removeItem(VIEW_AS_PREVIEW_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(
    VIEW_AS_PREVIEW_STORAGE_KEY,
    JSON.stringify({
      studentId: state.studentId,
      idpId: state.idpId,
      displayName: state.displayName,
      returnTo: state.returnTo,
    })
  );
}

const initialState: ViewAsPreviewState = readStoredPreview();

const previewSlice = createSlice({
  name: "preview",
  initialState,
  reducers: {
    startViewAsPreview: (
      state,
      action: PayloadAction<StartViewAsPreviewPayload>
    ) => {
      state.active = true;
      state.studentId = action.payload.studentId;
      state.idpId = action.payload.idpId;
      state.displayName = action.payload.displayName;
      state.returnTo = action.payload.returnTo || "/admin";
      persistPreview(state);
    },
    clearViewAsPreview: (state) => {
      state.active = false;
      state.studentId = "";
      state.idpId = "";
      state.displayName = "";
      state.returnTo = "/admin";
      persistPreview(state);
    },
  },
});

export const { startViewAsPreview, clearViewAsPreview } = previewSlice.actions;
export default previewSlice.reducer;
